/* AgenticMark — Marteu's animated agent indicator.
 *
 * A metaball-physics blob that conveys agent state in chat / workspace UI.
 * Three states:
 *   - state="rest"    → single coalesced orb drifting on a slow lissajous,
 *                       breathing gently. Reads as attentive / waiting.
 *   - state="working" → 0–4 "big" anchor particles + 4–12 "small" droplet
 *                       particles, gravity-pulled toward 2–14 dynamic
 *                       attractors that spawn, drift, and die. Reads as
 *                       active reasoning / doing.
 *   - state="static"  → the favicon M, no motion. For favicons, OG images,
 *                       PDF/print, accessibility-reduced-motion fallback.
 *
 * On every rest → working transition, the component:
 *   - rolls a fresh random particle count (anchors + droplets)
 *   - spawns the target number of attractors at a stratified spread
 *   - injects outward "explosion" velocity into every particle
 *   - lifts the velocity cap and disables gravity for ~420ms so the
 *     burst actually carries particles outward instead of being yanked
 *     back to attractors immediately.
 *
 * Implementation notes:
 *   - SVG with feGaussianBlur + feColorMatrix gooey filter; particles are
 *     rendered directly as ink-filled circles inside the filtered group,
 *     no enclosing rect/mask — the blob floats on the page background.
 *   - Particle physics (gravity-style force from each attractor, neighbor
 *     repulsion, per-tier inertial mass, sub-pixel jitter) runs at 60fps
 *     via requestAnimationFrame; falls back to setTimeout when the tab
 *     is hidden so the loop doesn't freeze on background tabs.
 *   - All tuning constants are locked into the TUNABLES object below.
 *     For live experimentation use the design/agentic-mark/ preview page
 *     in the marteu-freight repo.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

/* ============================================================
 * Constants — coordinate system + favicon path
 * ============================================================ */

const VBOX = '0 0 145.38 145.38';
const W    = 145.38;
const CX   = W / 2, CY = W / 2;

const M_PATH =
  'M116.29,40.79c-2.72-.02-3.14.09-5.86-.03-2.7-.12-4.42,1.84-4.42,1.84,0,0-26.43,39.48-26.44,39.45-.92-2.89-13.84-40.39-13.84-40.39,0,0-.35-.84-1.15-.8,0,0-2.87-.1-3.95-.05-.23,0-.45.08-.66.17-.99.42-1.69,1.4-2.3,2.52-10.36,18.74-21.07,37.42-31.56,56.1,0,0-.24.4-.24.9,0,1.31,1.07,1.31,2.35,1.29h8.07c1.17.01,1.73-.27,2.11-1.13,0,0,22.81-40.98,22.82-40.95,0,0,11.62,38.78,12.29,39.95,1.38,2.43,2.55,2.13,3.39,2.13h1c2.08,0,3.36-2.07,3.36-2.07,0,0,26.82-40.13,27.05-40.07,0,0,0,39.12,0,39.41,0,.47-.27,2.71,2.7,2.71,1.61,0,3.77.03,5.37,0,2.21-.04,3.14-.9,3.14-2.62,0-18.27,0-38.06,0-56.33,0-1.99-.99-2-3.22-2.02Z';

/* ============================================================
 * Tunables — locked-in defaults from the design tuning session.
 * For live experimentation use design/agentic-mark/ in this repo.
 * ============================================================ */

const T = {
  /* Random per-tier particle counts — rolled at every rest → working
     entry. Big = anchors (sluggish, large radius). Small = droplets
     (nimble, small radius). */
  PARTICLE_COUNT_BIG_MIN:   0,
  PARTICLE_COUNT_BIG_MAX:   4,
  PARTICLE_COUNT_SMALL_MIN: 4,
  PARTICLE_COUNT_SMALL_MAX: 12,

  /* Inertial mass — divides force during integration. Particles don't
     pull each other gravitationally; mass is purely about response speed. */
  BIG_PARTICLE_MASS:        1,
  SMALL_PARTICLE_MASS:      0.2,

  /* Working-mode attractor lifecycle. Attractors spawn, drift to a goal,
     periodically pick new goals, fade in/out, and die. */
  ATTRACTOR_COUNT_MIN:      6,
  ATTRACTOR_COUNT_MAX:      12,
  ATTRACTOR_COUNT_TARGET:   6,
  ATTRACTOR_LIFETIME_MIN:   1200,
  ATTRACTOR_LIFETIME_MAX:   3800,
  ATTRACTOR_FADE_MS:        1500,
  SPAWN_INTERVAL_MIN_MS:    300,
  SPAWN_INTERVAL_MAX_MS:    1000,
  GOAL_INTERVAL_MIN_MS:     900,
  GOAL_INTERVAL_MAX_MS:     2100,
  ATTRACTOR_SPEED_MIN:      0.85,
  ATTRACTOR_SPEED_MAX:      2,
  ATTRACTOR_STRENGTH_MIN:   90,
  ATTRACTOR_STRENGTH_MAX:   160,

  /* Gravity — force = strength × scale / dist², clamped at close range. */
  GRAVITY_SCALE:            0.5,
  GRAVITY_DIST2_MIN:        100,

  /* Particle dynamics. */
  PARTICLE_MAX_SPEED:       2,
  PARTICLE_DAMPING:         0.77,
  REPEL_DIST:               30,
  REPEL_STRENGTH:           0.5,

  /* Visual. */
  WORKING_BLUR_STD_DEV:     1.5,
  WORKING_BASE_R:           13,
  WORKING_BASE_R_SMALL:     6,
  GOOEY_SHARPNESS:          0,         /* 0 = soft glow, 1 = hard threshold */

  /* Spawn position bias — fraction of new attractors that land near an
     existing attractor (split feel) instead of pure random. */
  SPAWN_NEAR_EXISTING:      0,

  /* Rest → working "explosion" entry. */
  EXPLOSION_BURST_VEL:      6,
  EXPLOSION_DURATION_MS:    480,

  /* Aliveness effects. */
  BREATHING_ENABLED:        true,      /* rest only */
  BREATHING_PERIOD_MS:      6000,
  BREATHING_AMPLITUDE:      0.2,
  JITTER_ENABLED:           true,      /* working only */
  JITTER_AMPLITUDE:         0.2,
  BIRTH_SURGE_ENABLED:      true,
  BIRTH_SURGE_DURATION_MS:  510,
  BIRTH_SURGE_MULTIPLIER:   2,

  /* Rest-mode params. */
  REST_BASE_R:              22,
  REST_COHESION:            0.2,
  REST_BLUR_STD_DEV:        1,
  REST_DRIFT_AMPLITUDE:     2.5,       /* multiplier on the lissajous drift radius */
  REST_DRIFT_SPEED:         1.5,       /* multiplier on the lissajous frequencies */
};

/* ============================================================
 * Physics constants (not user-tunable)
 * ============================================================ */

const RADIUS_LERP = 0.08;
const SCALAR_LERP = 0.045;
const MAX_DT      = 3;
/* Internal-render supersampling factor for the gooey filter. The SVG
   element renders at SUPERSAMPLE × the display size; the wrapping div
   CSS-scales it down. This gives the filter SUPERSAMPLE² more pixels
   to work with, so blob edges anti-alias smoothly at chat-icon sizes
   instead of pixellating. 3× is a good middle ground; bump to 4× if
   icons are below ~32 px. */
const SUPERSAMPLE = 3;

/* ============================================================
 * Helpers
 * ============================================================ */

const randRange = (a, b) => a + Math.random() * (b - a);
const randInt   = (a, b) => a + Math.floor(Math.random() * (b - a + 1));
const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const randPoint = () => ({
  x: 18 + Math.random() * (W - 36),
  y: 18 + Math.random() * (W - 36),
});

const spawnAttractor = (now, existing) => {
  let p;
  if (existing && existing.length > 0 && Math.random() < T.SPAWN_NEAR_EXISTING) {
    const seed = existing[Math.floor(Math.random() * existing.length)];
    p = {
      x: Math.max(10, Math.min(W - 10, seed.x + (Math.random() - 0.5) * 28)),
      y: Math.max(10, Math.min(W - 10, seed.y + (Math.random() - 0.5) * 28)),
    };
  } else {
    p = randPoint();
  }
  const goal = randPoint();
  return {
    x: p.x, y: p.y,
    goalX: goal.x, goalY: goal.y,
    speed:    randRange(T.ATTRACTOR_SPEED_MIN, T.ATTRACTOR_SPEED_MAX),
    strength: randRange(T.ATTRACTOR_STRENGTH_MIN, T.ATTRACTOR_STRENGTH_MAX),
    bornAt:   now,
    lifetime: randRange(T.ATTRACTOR_LIFETIME_MIN, T.ATTRACTOR_LIFETIME_MAX),
    nextGoalAt: now + randRange(T.GOAL_INTERVAL_MIN_MS, T.GOAL_INTERVAL_MAX_MS),
  };
};

const effectiveStrength = (a, now) => {
  const age       = now - a.bornAt;
  const remaining = a.lifetime - age;
  const fade      = T.ATTRACTOR_FADE_MS;
  if (age < fade)        return a.strength * (age / fade);
  if (remaining < fade)  return a.strength * Math.max(0, remaining / fade);
  if (T.BIRTH_SURGE_ENABLED) {
    const surgeAge = age - fade;
    const surgeMs  = T.BIRTH_SURGE_DURATION_MS;
    if (surgeAge < surgeMs) {
      const t        = surgeAge / surgeMs;
      const peakMult = T.BIRTH_SURGE_MULTIPLIER;
      return a.strength * (1 + (peakMult - 1) * (1 - t));
    }
  }
  return a.strength;
};

const generateAttentive = (t, seed, anchorX, anchorY) => {
  const cx   = anchorX != null ? anchorX : CX;
  const cy   = anchorY != null ? anchorY : CY;
  const ampl = T.REST_DRIFT_AMPLITUDE;
  const sp   = T.REST_DRIFT_SPEED;
  const dx = Math.sin(t * 0.42 * sp + seed) * 16 * ampl + Math.sin(t * 0.13 * sp + seed * 2.3) * 4 * ampl;
  const dy = Math.cos(t * 0.36 * sp + seed * 1.6) * 12 * ampl + Math.cos(t * 0.11 * sp + seed * 1.9) * 3 * ampl;
  /* Clamp so the orb stays inside the viewBox even at peak breath. Without
     this, drift × breathing can push the orb past the boundary and the
     SVG clips it (visible at small render sizes like 50 px icons). */
  const peakR  = T.REST_BASE_R * (1 + (T.BREATHING_ENABLED ? T.BREATHING_AMPLITUDE : 0));
  const margin = peakR + 2;       /* tiny extra for the gooey blur halo */
  return {
    x: Math.max(margin, Math.min(W - margin, cx + dx)),
    y: Math.max(margin, Math.min(W - margin, cy + dy)),
  };
};

/* ============================================================
 * Core component — keyed by parent on rest → working transitions
 * so each working session starts with a fresh particle roll.
 * ============================================================ */

function MarkCore({ state, size, ink }) {
  /* Roll counts at mount. Because the parent re-keys this component on
     every rest → working transition, each working session gets a fresh
     random count of anchors and droplets. */
  const [counts] = useState(() => ({
    big:   randInt(T.PARTICLE_COUNT_BIG_MIN,   T.PARTICLE_COUNT_BIG_MAX),
    small: randInt(T.PARTICLE_COUNT_SMALL_MIN, T.PARTICLE_COUNT_SMALL_MAX),
  }));
  const N = counts.big + counts.small;

  const particlesRef   = useRef(null);
  const blurRef        = useRef(null);
  const colorMatrixRef = useRef(null);
  const psRef          = useRef(null);
  const attractorsRef  = useRef(null);
  const scalarsRef     = useRef(null);

  /* Stable filter id so React doesn't re-create the gradient on every render. */
  const gooId = useMemo(
    () => `agentic-mark-goo-${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  useEffect(() => {
    if (state === 'static') return;
    const particles = particlesRef.current;
    if (!particles) return;
    const circles = particles.children;

    /* Particle init — first counts.big are 'big' tier anchors, rest are
       'small' tier droplets. */
    if (!psRef.current || psRef.current.length !== N) {
      const nBig = counts.big;
      psRef.current = Array.from({ length: N }, (_, i) => ({
        x: CX + (Math.random() - 0.5) * 4,
        y: CY + (Math.random() - 0.5) * 4,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: 13,
        tier: i < nBig ? 'big' : 'small',
      }));
    }
    if (!attractorsRef.current) attractorsRef.current = [];
    if (!scalarsRef.current) {
      scalarsRef.current = { baseR: 13, blurStdDev: T.WORKING_BLUR_STD_DEV, repelMult: 1 };
    }

    const ps         = psRef.current;
    const attractors = attractorsRef.current;
    const scalars    = scalarsRef.current;

    const com = ps.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 });
    com.x /= N; com.y /= N;

    const isRest     = state === 'rest';
    const restAnchor = [com.x, com.y];
    const restSeed   = Math.random() * 100;
    const restAttr   = { x: com.x, y: com.y };

    /* Explosion on working entry — wipe attractors, spawn target count
       at stratified spread, inject outward radial velocity. */
    if (state === 'working') {
      attractors.length = 0;
      const targetN = T.ATTRACTOR_COUNT_TARGET;
      const startA  = Math.random() * Math.PI * 2;
      const nowMs   = performance.now();
      for (let k = 0; k < targetN; k++) {
        const a = startA + (k / targetN) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
        const r = 30 + Math.random() * 28;
        const goal = randPoint();
        attractors.push({
          x: CX + Math.cos(a) * r,
          y: CY + Math.sin(a) * r,
          goalX: goal.x, goalY: goal.y,
          speed:    randRange(T.ATTRACTOR_SPEED_MIN, T.ATTRACTOR_SPEED_MAX),
          strength: randRange(T.ATTRACTOR_STRENGTH_MIN, T.ATTRACTOR_STRENGTH_MAX),
          bornAt:   nowMs - T.ATTRACTOR_FADE_MS,
          lifetime: randRange(T.ATTRACTOR_LIFETIME_MIN, T.ATTRACTOR_LIFETIME_MAX) + T.ATTRACTOR_FADE_MS,
          nextGoalAt: nowMs + randRange(T.GOAL_INTERVAL_MIN_MS, T.GOAL_INTERVAL_MAX_MS),
        });
      }
      const burstVel = T.EXPLOSION_BURST_VEL;
      if (burstVel > 0) {
        for (let i = 0; i < N; i++) {
          const p = ps[i];
          const dx = p.x - com.x, dy = p.y - com.y;
          const d  = Math.hypot(dx, dy);
          const angle  = d > 0.5 ? Math.atan2(dy, dx) : Math.random() * Math.PI * 2;
          const jitter = (Math.random() - 0.5) * 0.6;
          p.vx += Math.cos(angle + jitter) * burstVel;
          p.vy += Math.sin(angle + jitter) * burstVel;
        }
      }
    }

    let nextSpawnAt   = performance.now() + randRange(T.SPAWN_INTERVAL_MIN_MS, T.SPAWN_INTERVAL_MAX_MS);
    let lastTime     = performance.now();
    const explodeUntil = state === 'working' ? performance.now() + T.EXPLOSION_DURATION_MS : 0;

    let cancelled = false, raf = 0, to = 0;
    const targetMs = 1000 / 60;

    const tick = () => {
      if (cancelled) return;
      const now = performance.now();
      const dt  = Math.min(MAX_DT, (now - lastTime) / 16.67);
      lastTime  = now;
      const tSec = now / 1000;

      let target;
      if (isRest) {
        const g = generateAttentive(tSec, restSeed, restAnchor[0], restAnchor[1]);
        restAttr.x = g.x; restAttr.y = g.y;
        target = { baseR: T.REST_BASE_R, blurStdDev: T.REST_BLUR_STD_DEV, repelMult: 0 };
      } else {
        /* Reap dead attractors. */
        for (let k = attractors.length - 1; k >= 0; k--) {
          if (now - attractors[k].bornAt >= attractors[k].lifetime) attractors.splice(k, 1);
        }
        while (attractors.length < T.ATTRACTOR_COUNT_MIN) {
          attractors.push(spawnAttractor(now, attractors));
        }
        if (now >= nextSpawnAt && attractors.length < T.ATTRACTOR_COUNT_MAX) {
          const overTarget = attractors.length - T.ATTRACTOR_COUNT_TARGET;
          const spawnProb  = overTarget <= 0 ? 1 : Math.max(0.15, 1 - overTarget * 0.18);
          if (Math.random() < spawnProb) {
            attractors.push(spawnAttractor(now, attractors));
          }
          nextSpawnAt = now + randRange(T.SPAWN_INTERVAL_MIN_MS, T.SPAWN_INTERVAL_MAX_MS);
        }
        for (let i = 0; i < attractors.length; i++) {
          const a = attractors[i];
          if (now >= a.nextGoalAt) {
            const g = randPoint();
            a.goalX = g.x; a.goalY = g.y;
            a.nextGoalAt = now + randRange(T.GOAL_INTERVAL_MIN_MS, T.GOAL_INTERVAL_MAX_MS);
          }
          const dx = a.goalX - a.x, dy = a.goalY - a.y;
          const dist = Math.hypot(dx, dy);
          if (dist > 0.01) {
            const move = Math.min(dist, a.speed * dt);
            a.x += (dx / dist) * move;
            a.y += (dy / dist) * move;
          }
        }
        target = { baseR: T.WORKING_BASE_R, blurStdDev: T.WORKING_BLUR_STD_DEV, repelMult: 1 };
      }

      scalars.baseR      += (target.baseR      - scalars.baseR)      * SCALAR_LERP;
      scalars.blurStdDev += (target.blurStdDev - scalars.blurStdDev) * SCALAR_LERP;
      scalars.repelMult  += (target.repelMult  - scalars.repelMult)  * SCALAR_LERP;

      if (blurRef.current) blurRef.current.setAttribute('stdDeviation', scalars.blurStdDev.toFixed(2));
      if (colorMatrixRef.current) {
        const s = T.GOOEY_SHARPNESS;
        const a = 4 + s * 14;
        const b = -1.4 - s * 5.6;
        colorMatrixRef.current.setAttribute(
          'values',
          `1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 ${a.toFixed(2)} ${b.toFixed(2)}`
        );
      }

      const repel       = scalars.repelMult * T.REPEL_STRENGTH;
      const maxStep     = T.PARTICLE_MAX_SPEED;
      const repelDist   = T.REPEL_DIST;
      const damping     = T.PARTICLE_DAMPING;
      const gravScale   = T.GRAVITY_SCALE;
      const gravD2Min   = T.GRAVITY_DIST2_MIN;
      const restCohesion = T.REST_COHESION;
      const exploding   = !isRest && now < explodeUntil;
      const baseR_big   = T.WORKING_BASE_R;
      const baseR_small = T.WORKING_BASE_R_SMALL;
      const restR       = T.REST_BASE_R;
      const massBig     = T.BIG_PARTICLE_MASS;
      const massSmall   = T.SMALL_PARTICLE_MASS;

      const breathMult = (T.BREATHING_ENABLED && isRest)
        ? 1 + Math.sin(now * 2 * Math.PI / T.BREATHING_PERIOD_MS) * T.BREATHING_AMPLITUDE
        : 1;
      const jitterAmp = (!isRest && !exploding && T.JITTER_ENABLED)
        ? T.JITTER_AMPLITUDE
        : 0;

      for (let i = 0; i < N; i++) {
        const p = ps[i];
        let ax = 0, ay = 0;

        if (isRest) {
          ax += (restAttr.x - p.x) * restCohesion;
          ay += (restAttr.y - p.y) * restCohesion;
        } else if (!exploding) {
          for (let k = 0; k < attractors.length; k++) {
            const a = attractors[k];
            const dx = a.x - p.x, dy = a.y - p.y;
            const d2 = Math.max(dx * dx + dy * dy, gravD2Min);
            const eff = effectiveStrength(a, now);
            const f   = eff * gravScale / d2;
            const d   = Math.sqrt(d2);
            ax += (dx / d) * f;
            ay += (dy / d) * f;
          }
        }

        if (repel > 0.001) {
          for (let j = 0; j < N; j++) {
            if (i === j) continue;
            const q = ps[j];
            const dx = p.x - q.x, dy = p.y - q.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < repelDist * repelDist && d2 > 0.01) {
              const d = Math.sqrt(d2);
              const f = (1 - d / repelDist) * repel;
              ax += (dx / d) * f;
              ay += (dy / d) * f;
            }
          }
        }

        const mass = p.tier === 'small' ? massSmall : massBig;
        p.vx = (p.vx + ax * dt / mass) * damping;
        p.vy = (p.vy + ay * dt / mass) * damping;
        if (!exploding) {
          const sp = Math.hypot(p.vx, p.vy);
          if (sp > maxStep) {
            p.vx *= maxStep / sp;
            p.vy *= maxStep / sp;
          }
        }
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        if (jitterAmp > 0) {
          p.x += (Math.random() - 0.5) * jitterAmp * 2;
          p.y += (Math.random() - 0.5) * jitterAmp * 2;
        }

        const tierR   = isRest ? restR : (p.tier === 'small' ? baseR_small : baseR_big);
        const targetR = tierR * breathMult;
        p.r += (targetR - p.r) * RADIUS_LERP;

        const c = circles[i];
        c.setAttribute('cx', p.x.toFixed(2));
        c.setAttribute('cy', p.y.toFixed(2));
        c.setAttribute('r',  p.r.toFixed(2));
      }

      schedule();
    };

    const schedule = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        raf = requestAnimationFrame(tick);
      } else {
        to = setTimeout(tick, targetMs);
      }
    };

    schedule();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (to)  clearTimeout(to);
    };
  }, [state, N, counts.big]);

  if (state === 'static') {
    return (
      <svg width={size} height={size} viewBox={VBOX} style={{ display: 'block' }}>
        <path d={M_PATH} fill={ink} />
      </svg>
    );
  }

  /* Wrap the SVG in a fixed-size span and render the SVG itself at
     SUPERSAMPLE × that size with a CSS scale-down transform. The browser
     rasterizes the gooey filter at the LARGER size and then smoothly
     downsamples — anti-aliased edges at small render sizes. The wrapping
     span clips the layout overflow so adjacent content isn't pushed. */
  const internalSize = size * SUPERSAMPLE;
  return (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        overflow: 'hidden',
        verticalAlign: 'top',
        lineHeight: 0,
      }}
    >
      <svg
        width={internalSize}
        height={internalSize}
        viewBox={VBOX}
        style={{
          display: 'block',
          transformOrigin: 'top left',
          transform: `scale(${1 / SUPERSAMPLE})`,
        }}
      >
        <defs>
          <filter id={gooId} x="-15%" y="-15%" width="130%" height="130%">
            <feGaussianBlur ref={blurRef} in="SourceGraphic" stdDeviation="6" result="blur" />
            <feColorMatrix
              ref={colorMatrixRef}
              in="blur"
              type="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 4 -1.4"
            />
          </filter>
        </defs>
        <g ref={particlesRef} filter={`url(#${gooId})`}>
          {Array.from({ length: N }).map((_, i) => (
            <circle key={i} cx={CX} cy={CY} r="14.5" fill={ink} />
          ))}
        </g>
      </svg>
    </span>
  );
}

/* ============================================================
 * Public component — wrapper that re-keys MarkCore on every
 * rest → working transition so each working session starts
 * with a fresh random particle count and a fresh explosion.
 * ============================================================ */

export default function AgenticMark({
  state = 'rest',
  size  = 36,
  ink   = '#22201C',
}) {
  /* Mutating a ref during render is fine; refs are not part of render
     output. We use this to detect rest → working transitions without
     incurring an extra render or effect cycle. */
  const sessionRef = useRef({ id: 0, prev: state });
  if (state === 'working' && sessionRef.current.prev !== 'working') {
    sessionRef.current.id += 1;
  }
  sessionRef.current.prev = state;

  return (
    <MarkCore
      key={sessionRef.current.id}
      state={state}
      size={size}
      ink={ink}
    />
  );
}
